// @givmo/board — Governance: the board's governing documents and the meeting rules recorded from
// them, as one page with two parts (D-CBG-9, 2026-09-28: "there could just be a page called
// GOvernance that has a place with the docs and a palce with governance params"), since
// CBG-YR3-ROLES layer 2 a third, Officers: the board's own titles (`l2-officers`), and since
// CBG-YR3-TERMS a fourth between them, Terms: the board's own term rules and classes (frame t2).
//
// The parts are the former Governing documents and Governance parameters pages, unchanged inside:
// the same reads, the same dialogs, the same permission gates. What the page adds is the choice
// between them, carried in the address (`?tab=documents` · `?tab=meeting-rules` · `?tab=terms` · `?tab=officers`, the Profile page's
// deep-linkable `?tab=` precedent), and written back with `replace` so switching parts never stacks
// history. An address with no part, or a part this build does not know, opens Documents. The two
// former addresses land here on their own part — the host's redirects name it.

import { Tabs, type TabsValue } from '@givmo/design-system';
import {
  GOVERNANCE_TABS,
  BOARD_PAGES,
  isGovernanceTab,
  type GovernanceTab,
} from '../../board-routes';
import { useNavigate, useSearch } from '../../board-routing';
import { PageShell } from '../../components/page-shell';
import { GoverningDocumentsPart } from '../govdocs/govdocs-page';
import { MeetingRulesPart } from '../parameters/parameters-page';
import { officerRolesL2Words as l2 } from '../roster/officer-roles-l2-words';
import { OfficersPart } from './officers-part';
import { TermsPart } from './terms-part';
import { termsWords as tw } from '../roster/terms-words';

/** The part the address names, or Documents. */
export function governanceTabOf(search: Record<string, unknown>): GovernanceTab {
  const tab = search.tab;
  return isGovernanceTab(tab) ? tab : GOVERNANCE_TABS.documents;
}

export function GovernancePage() {
  const search = useSearch();
  const navigate = useNavigate();
  const tab = governanceTabOf(search);

  const selectTab = (value: TabsValue) => {
    const next = isGovernanceTab(value) ? value : GOVERNANCE_TABS.documents;
    void navigate({ to: BOARD_PAGES.governance, search: { tab: next }, replace: true });
  };

  return (
    <PageShell title="Governance" testId="page-governance">
      <Tabs.Root appearance="saas" value={tab} onValueChange={selectTab} testId="governance-tabs">
        <Tabs.List aria-label="Governance">
          <Tabs.Tab value={GOVERNANCE_TABS.documents} testId="governance-tab-documents">
            Documents
          </Tabs.Tab>
          <Tabs.Tab value={GOVERNANCE_TABS.meetingRules} testId="governance-tab-meeting-rules">
            Meeting rules
          </Tabs.Tab>
          <Tabs.Tab value={GOVERNANCE_TABS.terms} testId="governance-tab-terms">
            {tw.tab}
          </Tabs.Tab>
          <Tabs.Tab value={GOVERNANCE_TABS.officers} testId="governance-tab-officers">
            {l2.officersTab}
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value={GOVERNANCE_TABS.documents} focusable={false}>
          <GoverningDocumentsPart />
        </Tabs.Panel>
        <Tabs.Panel value={GOVERNANCE_TABS.meetingRules} focusable={false}>
          <MeetingRulesPart />
        </Tabs.Panel>
        <Tabs.Panel value={GOVERNANCE_TABS.terms} focusable={false}>
          <TermsPart />
        </Tabs.Panel>
        <Tabs.Panel value={GOVERNANCE_TABS.officers} focusable={false}>
          <OfficersPart />
        </Tabs.Panel>
      </Tabs.Root>
    </PageShell>
  );
}
